import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Fade,
  Link as MuiLink,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { useNotification } from '../../context/NotificationContext';
import authService from '../../services/auth.service';
import AuthLayout from '../../components/layout/AuthLayout';
import PasswordStrengthBar from '../../components/common/PasswordStrengthBar';
import OtpInput from '../../components/common/OtpInput';
import axios from 'axios';

type Step = 'form' | 'verify' | 'success';

const cardSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
  bgcolor: 'rgba(20, 20, 30, 0.58)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings, loading: settingsLoading } = usePublicSettings();
  const { showNotification } = useNotification();

  const [step, setStep] = useState<Step>('form');

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [emailError, setEmailError] = useState('');
  const [domainChecking, setDomainChecking] = useState(false);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Success auto-redirect
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  // Feature flag guard
  useEffect(() => {
    if (!settingsLoading && settings && !settings.selfRegistrationEnabled) {
      navigate('/login', { replace: true });
    }
  }, [settingsLoading, settings, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown]);

  // Success auto-redirect
  useEffect(() => {
    if (step !== 'success') return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, navigate]);

  // Validation helpers
  const isEmailValid = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const isPasswordValid = (val: string) => {
    return val.length >= 8 && /[A-Z]/.test(val) && /[a-z]/.test(val) && /\d/.test(val);
  };

  const firstNameError = touched.firstName && (!firstName.trim() || firstName.trim().length < 2 || firstName.trim().length > 50);
  const lastNameError = touched.lastName && (!lastName.trim() || lastName.trim().length < 2 || lastName.trim().length > 50);
  const confirmPasswordError = touched.confirmPassword && confirmPassword !== password;
  const passwordInvalid = touched.password && !isPasswordValid(password);

  const formValid =
    firstName.trim().length >= 2 &&
    firstName.trim().length <= 50 &&
    lastName.trim().length >= 2 &&
    lastName.trim().length <= 50 &&
    isEmailValid(email) &&
    !emailError &&
    isPasswordValid(password) &&
    confirmPassword === password;

  // Email domain check on blur
  const handleEmailBlur = useCallback(async () => {
    setTouched((prev) => ({ ...prev, email: true }));
    if (!email || !isEmailValid(email)) {
      if (email && !isEmailValid(email)) {
        setEmailError('Please enter a valid email address');
      }
      return;
    }
    setEmailError('');
    setDomainChecking(true);
    try {
      const res = await authService.checkDomain(email);
      if (!res.data.allowed) {
        setEmailError(res.data.message || 'Personal email addresses are not accepted. Please use your corporate email.');
      }
    } catch {
      // Non-blocking — real validation happens server-side
    } finally {
      setDomainChecking(false);
    }
  }, [email]);

  // Submit registration form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid) return;

    setLoading(true);
    setError('');

    try {
      await authService.initiateRegistration({
        email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      setStep('verify');
      setResendCooldown(60);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const message = err.response?.data?.message;
        if (status === 409) {
          setError('An account with this email already exists. Sign in instead.');
        } else if (status === 400) {
          setError(message || 'Invalid input. Please check your details.');
        } else if (status === 403) {
          showNotification('Registration is currently unavailable.', 'error');
          navigate('/login', { replace: true });
          return;
        } else if (status === 429) {
          setError('Please wait before trying again.');
        } else {
          setError(message || 'Registration failed. Please try again.');
        }
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerify = async () => {
    if (otpValue.length < 6) return;

    setLoading(true);
    setVerifyError('');
    setOtpError(false);

    try {
      const verifyRes = await authService.verifyRegistration({ email, code: otpValue });
      const token = verifyRes.data.token;

      await authService.completeRegistration({ email, token });
      setStep('success');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        showNotification('Registration is currently unavailable.', 'error');
        navigate('/login', { replace: true });
        return;
      }
      setOtpError(true);
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.message;
        setVerifyError(message || 'Invalid code. Please try again.');
      } else {
        setVerifyError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit on 6 digits
  const handleOtpChange = (val: string) => {
    setOtpValue(val);
    setOtpError(false);
    setVerifyError('');
  };

  useEffect(() => {
    if (otpValue.length === 6 && step === 'verify' && !loading) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue]);

  // Resend code
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authService.initiateRegistration({
        email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      setResendCooldown(60);
      setOtpValue('');
      setOtpError(false);
      setVerifyError('');
      showNotification('New code sent', 'success');
    } catch {
      showNotification('Failed to resend code', 'error');
    }
  };

  // Back to form — preserves fields
  const handleBack = () => {
    setStep('form');
    setOtpValue('');
    setOtpError(false);
    setVerifyError('');
  };

  // Loading state
  if (settingsLoading) {
    return (
      <AuthLayout maxWidth="480px">
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'rgba(255,255,255,0.6)' }} />
        </Box>
      </AuthLayout>
    );
  }

  // === STEP 3: SUCCESS ===
  if (step === 'success') {
    return (
      <AuthLayout maxWidth="480px">
        <Fade in>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '48px', textAlign: 'center' }}>
              <CheckCircleOutline sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
                Account created successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                You can now sign in with your email and password.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                component={Link}
                to="/login"
                sx={{ height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                Go to Sign In
              </Button>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 2, display: 'block' }}>
                Redirecting in {redirectCountdown}s...
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      </AuthLayout>
    );
  }

  // === STEP 2: VERIFY ===
  if (step === 'verify') {
    return (
      <AuthLayout maxWidth="480px">
        <Fade in>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '48px' }}>
              <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
                Verify your email
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                We've sent a 6-digit code to <strong style={{ color: '#fff' }}>{email}</strong>. Check your inbox.
              </Typography>

              {verifyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {verifyError}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <OtpInput
                  value={otpValue}
                  onChange={handleOtpChange}
                  error={otpError}
                  disabled={loading}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleVerify}
                disabled={loading || otpValue.length < 6}
                sx={{ height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
              </Box>

              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleBack}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                >
                  &larr; Back
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </AuthLayout>
    );
  }

  // === STEP 1: FORM ===
  return (
    <AuthLayout maxWidth="480px">
      <Fade in>
        <Card sx={cardSx}>
          <CardContent sx={{ p: { xs: '24px', sm: '40px' } }}>
            <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
              Create your account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                {error.includes('already exists') && (
                  <>
                    {' '}
                    <MuiLink component={Link} to="/login" sx={{ color: 'inherit', fontWeight: 600 }}>
                      Sign in
                    </MuiLink>
                  </>
                )}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
                  error={!!firstNameError}
                  helperText={firstNameError ? 'Required (2-50 characters)' : ' '}
                  disabled={loading}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' } }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                  sx={inputFieldSx}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
                  error={!!lastNameError}
                  helperText={lastNameError ? 'Required (2-50 characters)' : ' '}
                  disabled={loading}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' } }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                  sx={inputFieldSx}
                />
              </Box>

              <TextField
                fullWidth
                label="Corporate Email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                onBlur={handleEmailBlur}
                error={!!(touched.email && emailError)}
                helperText={touched.email && emailError ? emailError : (domainChecking ? 'Checking...' : ' ')}
                disabled={loading}
                margin="normal"
                variant="outlined"
                InputProps={{ sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' } }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                sx={inputFieldSx}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                error={!!passwordInvalid}
                helperText={passwordInvalid ? 'Min 8 chars, 1 uppercase, 1 lowercase, 1 digit' : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 digit'}
                disabled={loading}
                margin="normal"
                variant="outlined"
                InputProps={{
                  sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                FormHelperTextProps={{ sx: { color: passwordInvalid ? undefined : 'rgba(255,255,255,0.35)' } }}
                sx={inputFieldSx}
              />
              <PasswordStrengthBar password={password} />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, confirmPassword: true }))}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError ? 'Passwords do not match' : ' '}
                disabled={loading}
                margin="normal"
                variant="outlined"
                InputProps={{
                  sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                sx={inputFieldSx}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !formValid}
                sx={{ mt: 3, height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', mt: 2, fontSize: '0.85rem' }}>
              By creating an account, you agree to our{' '}
              <MuiLink
                component={Link}
                to="/privacy-policy"
                sx={{ color: '#90caf9', fontWeight: 500, textDecoration: 'underline', '&:hover': { color: '#fff' } }}
              >
                Privacy Policy
              </MuiLink>
              .
            </Typography>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', mt: 2 }}>
              Already have an account?{' '}
              <MuiLink
                component={Link}
                to="/login"
                sx={{ color: '#90caf9', fontWeight: 500, textDecoration: 'underline', '&:hover': { color: '#fff' } }}
              >
                Sign in
              </MuiLink>
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </AuthLayout>
  );
};

export default RegisterPage;
